/* Monaco editor integration — lazy-loaded from CDN, no build step.
 *
 * Replaces read-only file viewer with an editable Monaco instance
 * when a workspace tab is opened. Falls back to the original renderer
 * if Monaco fails to load.
 */

import { state, signals, refs, esc } from "./state.js";
import { api } from "./api.js";
import { getAuthMode } from "./auth.js";
import { notify } from "./toast.js";
import { getEffectiveTheme, buildMonacoThemeRules } from "./theme.js";

// ---------------------------------------------------------------------------
// State
// ---------------------------------------------------------------------------

const MONACO_CDN = "https://cdn.jsdelivr.net/npm/monaco-editor@0.52.0/min";
let monacoReady = false;
let monacoLoading = false;
let loadPromise = null;
let editorInstance = null;      // current Monaco editor instance
let activeTabId = null;         // which tab the editor is showing
const tabModels = new Map();    // tabId → {model, viewState, dirty}
const tabOriginal = new Map();  // tabId → original content (for dirty detection)

// Validation markers per tab
const tabMarkers = new Map();   // tabId → [{line, column, message, severity}]

// Validation bar state: null | 'valid' | 'errors' | 'stale' | 'validating'
let _validationState = null;

// ---------------------------------------------------------------------------
// Theme integration
// ---------------------------------------------------------------------------

const MONACO_THEME_LIGHT = 'agentcpq-light';
const MONACO_THEME_DARK = 'agentcpq-dark';
let _monacoThemesDefined = false;

function _activeMonacoThemeName() {
  return getEffectiveTheme() === 'dark' ? MONACO_THEME_DARK : MONACO_THEME_LIGHT;
}

function _applyMonacoCustomTheme(monaco) {
  if (!monaco) return;
  const rules = buildMonacoThemeRules();
  // Light variant
  monaco.editor.defineTheme(MONACO_THEME_LIGHT, {
    base: 'vs',
    inherit: true,
    rules,
    colors: {},
  });
  // Dark variant — foreground colors from the same user palette; base vs-dark handles bg.
  monaco.editor.defineTheme(MONACO_THEME_DARK, {
    base: 'vs-dark',
    inherit: true,
    rules,
    colors: {},
  });
  _monacoThemesDefined = true;
}

function _hookThemeEvents() {
  window.addEventListener('themechange', () => {
    const monaco = window.monaco;
    if (!monaco) return;
    _applyMonacoCustomTheme(monaco);
    monaco.editor.setTheme(_activeMonacoThemeName());
  });
  window.addEventListener('editorcolorschange', () => {
    const monaco = window.monaco;
    if (!monaco) return;
    _applyMonacoCustomTheme(monaco);
    monaco.editor.setTheme(_activeMonacoThemeName());
  });
}
_hookThemeEvents();

// ---------------------------------------------------------------------------
// Lazy Monaco loading
// ---------------------------------------------------------------------------

export function isMonacoReady() { return monacoReady; }

async function ensureMonaco() {
  if (monacoReady) return true;
  if (loadPromise) return loadPromise;
  loadPromise = _loadMonaco();
  return loadPromise;
}

async function _loadMonaco() {
  if (monacoLoading) return false;
  monacoLoading = true;

  try {
    // Inject AMD loader script
    await new Promise((resolve, reject) => {
      const script = document.createElement("script");
      script.src = `${MONACO_CDN}/vs/loader.js`;
      script.onload = resolve;
      script.onerror = () => reject(new Error("Failed to load Monaco loader"));
      document.head.appendChild(script);
    });

    // Configure AMD paths
    window.require.config({ paths: { vs: `${MONACO_CDN}/vs` } });

    // Load Monaco editor main module
    await new Promise((resolve, reject) => {
      window.require(["vs/editor/editor.main"], () => {
        _registerIronPython();
        monacoReady = true;
        resolve();
      }, reject);
    });

    return true;
  } catch (err) {
    console.error("Monaco load failed:", err);
    monacoLoading = false;
    loadPromise = null;
    return false;
  }
}

// ---------------------------------------------------------------------------
// IronPython language registration
// ---------------------------------------------------------------------------

function _registerIronPython() {
  const monaco = window.monaco;
  if (!monaco) return;

  monaco.languages.register({ id: "ironpython" });

  monaco.languages.setMonarchTokensProvider("ironpython", {
    keywords: [
      "and", "as", "assert", "break", "class", "continue", "def", "del",
      "elif", "else", "except", "exec", "finally", "for", "from", "global",
      "if", "import", "in", "is", "lambda", "not", "or", "pass", "print",
      "raise", "return", "try", "while", "with", "yield",
    ],
    builtins: [
      "True", "False", "None", "str", "int", "float", "bool", "list",
      "dict", "tuple", "set", "len", "range", "enumerate", "zip", "map",
      "filter", "sorted", "abs", "round", "isinstance", "type", "hasattr",
      "getattr", "setattr",
    ],
    cpqObjects: [
      "Quote", "Item", "User", "Product", "context", "sender", "arg",
      "Trace", "Log", "SqlHelper", "RestClient", "AuthorizedRestClient",
      "WebServiceHelper", "XmlHelper", "JsonHelper", "ScriptExecutor",
      "ProductHelper", "Session", "UserPersonalizationHelper",
      "EventArgs", "QuoteHelper",
    ],
    tokenizer: {
      root: [
        [/#.*$/, "comment"],
        [/"""/, "string", "@tripleString"],
        [/'''/, "string", "@tripleStringSingle"],
        [/"(?:[^"\\]|\\.)*"/, "string"],
        [/'(?:[^'\\]|\\.)*'/, "string"],
        [/\d+(\.\d+)?/, "number"],
        [/[a-zA-Z_]\w*/, {
          cases: {
            "@keywords": "keyword",
            "@builtins": "type.identifier",
            "@cpqObjects": "variable.predefined",
            "@default": "identifier",
          },
        }],
        [/[{}()[\]]/, "@brackets"],
        [/[=!<>]=?|[+\-*/%&|^~]/, "operator"],
        [/[;,.]/, "delimiter"],
      ],
      tripleString: [
        [/"""/, "string", "@pop"],
        [/./, "string"],
      ],
      tripleStringSingle: [
        [/'''/, "string", "@pop"],
        [/./, "string"],
      ],
    },
  });
}

// ---------------------------------------------------------------------------
// Editor lifecycle
// ---------------------------------------------------------------------------

/**
 * Mount a Monaco editor into the given container for the given tab.
 * If Monaco isn't loaded yet, triggers loading and calls back when ready.
 * Returns true if Monaco is rendering, false if fallback should be used.
 */
export async function mountEditor(container, tab) {
  const ready = await ensureMonaco();
  if (!ready) return false;

  const monaco = window.monaco;
  if (!monaco) return false;

  // Save view state of previous tab
  if (editorInstance && activeTabId) {
    const prevState = tabModels.get(activeTabId);
    if (prevState) {
      prevState.viewState = editorInstance.saveViewState();
    }
  }

  // Get or create model for this tab
  let tabState = tabModels.get(tab.id);
  if (!tabState) {
    const lang = _monacoLanguage(tab.language);
    const uri = monaco.Uri.parse(`file:///${tab.path || tab.id}`);
    const model = monaco.editor.createModel(tab._content || "", lang, uri);
    tabState = { model, viewState: null, dirty: false };
    tabModels.set(tab.id, tabState);
    tabOriginal.set(tab.id, tab._content || "");

    // Track changes for dirty indicator
    model.onDidChangeContent(() => {
      const original = tabOriginal.get(tab.id) || "";
      const isDirty = model.getValue() !== original;
      tabState.dirty = isDirty;
      _updateDirtyBadge(tab.id, isDirty);

      // Mark validation as stale if we had a result
      if (_validationState === 'valid' || _validationState === 'errors') {
        updateValidationBar('stale');
      }
    });
  }

  // Dispose previous editor instance — renderFileViewer creates a fresh
  // container each time, so the old DOM node is detached.  Re-create
  // the editor in the new container to avoid the blank-editor bug.
  if (editorInstance) {
    editorInstance.dispose();
    editorInstance = null;
  }

  _applyMonacoCustomTheme(monaco);
  editorInstance = monaco.editor.create(container, {
    model: tabState.model,
    theme: _activeMonacoThemeName(),
    fontSize: 13,
    fontFamily: getComputedStyle(document.documentElement).getPropertyValue("--mono").trim() || "'JetBrains Mono', 'IBM Plex Mono', ui-monospace, monospace",
    minimap: { enabled: false },
    lineNumbers: "on",
    renderLineHighlight: "line",
    scrollBeyondLastLine: false,
    wordWrap: "on",
    automaticLayout: true,
    tabSize: 4,
    insertSpaces: true,
    overviewRulerLanes: 0,
    hideCursorInOverviewRuler: true,
    fixedOverflowWidgets: true,
    padding: { top: 8 },
    // Hosted: the agent applies file changes (audited + undoable), so the editor is a
    // reader — no unsaved buffer to lose, nothing to smuggle past the agent. The lock
    // banner above the editor (search.js) explains this and hands off to the composer.
    readOnly: getAuthMode() === "hosted",
  });

  // Keyboard shortcuts
  editorInstance.addCommand(monaco.KeyMod.CtrlCmd | monaco.KeyCode.KeyS, () => {
    saveCurrentTab();
  });

  // Send-selection-to-agent actions (right-click menu). `editorHasSelection`
  // is a built-in Monaco context key — these auto-disable with no selection.
  const _emitSelection = (verb) => {
    const ctx = getSelectionContext();
    if (ctx) window.dispatchEvent(new CustomEvent("send-selection-to-agent", { detail: { verb, ctx } }));
  };
  editorInstance.addAction({ id: "agentcpq.explain", label: "Explain selection (AgentCPQ)", contextMenuGroupId: "agentcpq", contextMenuOrder: 1, precondition: "editorHasSelection", run: () => _emitSelection("explain") });
  editorInstance.addAction({ id: "agentcpq.fix", label: "Fix selection (AgentCPQ)", contextMenuGroupId: "agentcpq", contextMenuOrder: 2, precondition: "editorHasSelection", run: () => _emitSelection("fix") });
  editorInstance.addAction({ id: "agentcpq.send", label: "Send selection to agent", contextMenuGroupId: "agentcpq", contextMenuOrder: 3, precondition: "editorHasSelection", run: () => _emitSelection("send") });

  // Status bar: cursor position
  editorInstance.onDidChangeCursorPosition((e) => {
    const sbCursor = document.getElementById('sbCursor');
    if (sbCursor) sbCursor.textContent = `Ln ${e.position.lineNumber}, Col ${e.position.column}`;
  });

  // Status bar: language + indent
  _updateStatusBarLang(tab.language);
  _updateStatusBarIndent(editorInstance);

  // Restore view state (cursor, scroll)
  if (tabState.viewState) {
    editorInstance.restoreViewState(tabState.viewState);
  }

  // Restore markers
  const markers = tabMarkers.get(tab.id);
  if (markers) {
    _setMarkers(tabState.model, markers);
  }

  // Highlight line if requested
  if (tab.highlightLine) {
    editorInstance.revealLineInCenter(tab.highlightLine);
    editorInstance.setPosition({ lineNumber: tab.highlightLine, column: 1 });
  }

  activeTabId = tab.id;
  editorInstance.focus();
  return true;
}

/** Dispose editor model when tab is closed. */
export function disposeTabModel(tabId) {
  const s = tabModels.get(tabId);
  if (s) {
    s.model.dispose();
    tabModels.delete(tabId);
  }
  tabOriginal.delete(tabId);
  tabMarkers.delete(tabId);
}

/** Insert text at the current cursor position (or replace selection).
 *  Integrates with undo/redo. Returns true if insert succeeded. */
export function insertAtCursor(text) {
  if (!editorInstance || !activeTabId) return false;
  const selection = editorInstance.getSelection();
  editorInstance.executeEdits("knowledge-insert", [{
    range: selection,
    text,
    forceMoveMarkers: true,
  }]);
  editorInstance.focus();
  return true;
}

let _traceDecorations = null;

/**
 * Reveal a line and paint a transient highlight on it — used by trace→code
 * navigation. No-op until Monaco + an editor instance exist.
 */
export function highlightTraceLine(line) {
  const ed = editorInstance;
  if (!ed || !window.monaco || !line) return;
  ed.revealLineInCenter(line);
  ed.setPosition({ lineNumber: line, column: 1 });
  if (!_traceDecorations) _traceDecorations = ed.createDecorationsCollection();
  _traceDecorations.set([{
    range: new window.monaco.Range(line, 1, line, 1),
    options: { isWholeLine: true, className: "trace-jump-line", glyphMarginClassName: "trace-jump-glyph" },
  }]);
  setTimeout(() => { try { if (_traceDecorations) _traceDecorations.clear(); } catch { /* disposed */ } }, 2500);
}

/**
 * Capture the current editor selection + its file context, for "send selection
 * to agent". Returns null when there is no non-empty selection.
 * @returns {{text:string, name:string, path:?string, language:string, startLine:number, endLine:number}|null}
 */
export function getSelectionContext() {
  if (!editorInstance || !activeTabId) return null;
  const sel = editorInstance.getSelection();
  const model = editorInstance.getModel();
  if (!sel || !model) return null;
  const text = model.getValueInRange(sel);
  if (!text || !text.trim()) return null;
  const tab = state.workspaceTabs.find((t) => t.id === activeTabId);
  const name = tab ? (tab.label || (tab.path ? tab.path.split("/").pop() : "selection")) : "selection";
  return {
    text,
    name,
    path: tab ? tab.path : null,
    language: tab ? tab.language || "" : "",
    startLine: sel.startLineNumber,
    endLine: sel.endLineNumber,
  };
}

/** Navigate to a specific line in the active editor. */
export function revealLine(line) {
  // Use editorInstance if available, otherwise find the active Monaco editor
  let editor = editorInstance;
  if (!editor && window.monaco) {
    const editors = window.monaco.editor.getEditors();
    if (editors.length) editor = editors[0];
  }
  if (!editor) return;
  editor.setPosition({ lineNumber: line, column: 1 });
  editor.revealLineInCenter(line);
  // Defer focus to next microtask — synchronous focus() during click events
  // gets overridden by the browser's focus management.
  setTimeout(() => editor.focus(), 0);
}

/** Subscribe to content changes on the active tab's model.
 *  Returns a dispose function, or null if no editor. */
export function onContentChange(callback) {
  if (!editorInstance || !activeTabId) return null;
  const s = tabModels.get(activeTabId);
  if (!s) return null;
  const disposable = s.model.onDidChangeContent(() => {
    callback(s.model.getValue());
  });
  return () => disposable.dispose();
}

/** Get the current editor content for the active tab. */
export function getEditorContent() {
  if (!editorInstance || !activeTabId) return null;
  const s = tabModels.get(activeTabId);
  return s ? s.model.getValue() : null;
}

/** Check if a tab has unsaved changes. */
export function isTabDirty(tabId) {
  const s = tabModels.get(tabId || activeTabId);
  return s ? s.dirty : false;
}

// ---------------------------------------------------------------------------
// Save
// ---------------------------------------------------------------------------

export async function saveCurrentTab() {
  if (!activeTabId) return;
  const tab = state.workspaceTabs.find(t => t.id === activeTabId);
  if (!tab || !tab.path) return;
  const s = tabModels.get(activeTabId);
  if (!s) return;

  // Hosted: never write directly. /api/files/* is not on the hosted allowlist (a direct
  // write would just 404); the agent hand-off is the product model, keeping every change
  // audited and undoable. One intercept here covers every save entry point (editor
  // Cmd+S, global Cmd+S): toast the model, hand off to the agent.
  if (getAuthMode() === "hosted") {
    notify("The agent applies changes in the hosted app. I have started a request for this file below.", "info");
    window.dispatchEvent(new CustomEvent("agent-prefill-composer", {
      detail: { text: `Please edit ${tab.path}: ` },
    }));
    return;
  }

  const content = s.model.getValue();
  // Save is local-only. Pushing to cloud requires an explicit sync action.
  const isSyncable = /^(global-scripts|custom-calculations|ui-templates)\//.test(tab.path);
  const displayName = tab.label || tab.path.split('/').pop();
  const t = notify(`Saving ${displayName}…`, 'progress');

  try {
    await api.writeFile(tab.path, content);
    tab._content = content;
    tabOriginal.set(activeTabId, content);
    s.dirty = false;
    _updateDirtyBadge(activeTabId, false);

    t.resolve(`Saved ${displayName}`, 'success');

    // Code on disk changed — bump so the live Execution Order view refreshes.
    signals.codebaseRevision.update((n) => n + 1);

    if (isSyncable) {
      // file-tree listens for this and refreshes sync badges so the user can
      // see the file is now ahead of cloud and push it explicitly.
      window.dispatchEvent(new CustomEvent('sync-refresh-required'));
    }
  } catch (err) {
    console.error("Save failed:", err);
    t.resolve(`Save failed: ${err.message}`, 'error');
  }
}

// ---------------------------------------------------------------------------
// Validation markers (Gap #6: error-to-line linking)
// ---------------------------------------------------------------------------

/**
 * Set validation error markers on a tab's editor model.
 * @param {string} tabId - Tab to set markers on (defaults to active)
 * @param {Array<{line?: number, column?: number, endLine?: number, endColumn?: number, message: string, severity?: string}>} errors
 */
export function setValidationMarkers(tabId, errors) {
  tabId = tabId || activeTabId;
  tabMarkers.set(tabId, errors);

  const s = tabModels.get(tabId);
  if (!s) return;
  _setMarkers(s.model, errors);
}

/** Clear all validation markers for a tab. */
export function clearValidationMarkers(tabId) {
  tabId = tabId || activeTabId;
  tabMarkers.delete(tabId);
  const s = tabModels.get(tabId);
  if (s && window.monaco) {
    window.monaco.editor.setModelMarkers(s.model, "validation", []);
  }
}

function _setMarkers(model, errors) {
  const monaco = window.monaco;
  if (!monaco) return;

  const markers = errors.map(e => {
    const line = e.line || _guessLineFromMessage(e.message, model);
    return {
      severity: e.severity === "warning"
        ? monaco.MarkerSeverity.Warning
        : monaco.MarkerSeverity.Error,
      message: e.message || String(e),
      startLineNumber: line || 1,
      startColumn: e.column || 1,
      endLineNumber: e.endLine || line || 1,
      endColumn: e.endColumn || model.getLineMaxColumn(e.endLine || line || 1),
    };
  });

  monaco.editor.setModelMarkers(model, "validation", markers);
}

/** Decode common HTML entities in SAP CPQ error strings. */
function _decodeHtmlEntities(s) {
  return s
    .replace(/&#(\d+);/g, (_, n) => String.fromCharCode(parseInt(n)))
    .replace(/&amp;/g, "&")
    .replace(/&lt;/g, "<")
    .replace(/&gt;/g, ">");
}

/**
 * Parse SAP CPQ validation error strings.
 *
 * The API returns one string that may contain multiple errors:
 *   "Error: msg1<br/>Line: 1<br/>Position: 5, Error: msg2<br/>Line: 2<br/>Position: 3"
 *
 * Returns an array of {message, line, column} objects.
 */
function _parseCpqErrors(raw) {
  const decoded = _decodeHtmlEntities(raw);

  // Split on ", Error:" to separate multiple errors within one string
  const chunks = decoded.split(/,\s*(?=Error:)/i);
  const results = [];

  for (const chunk of chunks) {
    const parts = chunk.split(/<br\s*\/?>/i);
    const msg = (parts[0] || chunk).trim();
    let line = null, column = null;

    for (const part of parts) {
      const lm = part.match(/Line:\s*(\d+)/i);
      if (lm) line = parseInt(lm[1]);
      const pm = part.match(/Position:\s*(\d+)/i);
      if (pm) column = parseInt(pm[1]);
    }

    results.push({ message: msg, line, column });
  }

  return results;
}

/** Try to extract a line number from common error message patterns. */
function _guessLineFromMessage(msg, model) {
  if (!msg) return null;
  // "Line: 2", "line 12", "Line 12:", "(12, 5)"
  const m = msg.match(/(?:line\s*[:=]?\s*|\()(\d+)/i);
  if (m) {
    const line = parseInt(m[1]);
    if (line > 0 && line <= model.getLineCount()) return line;
  }
  return null;
}

// ---------------------------------------------------------------------------
// Validate current tab
// ---------------------------------------------------------------------------

export async function validateCurrentTab() {
  if (!activeTabId) return null;
  const content = getEditorContent();
  if (!content) return null;

  try {
    const res = await api.validateScript(content);
    const result = res?.result || res;
    if (result?.valid) {
      clearValidationMarkers(activeTabId);
      return { valid: true };
    } else {
      const errors = (result?.errors || []).flatMap(e =>
        typeof e === "string" ? _parseCpqErrors(e) : [e]
      );
      setValidationMarkers(activeTabId, errors);
      return { valid: false, errors };
    }
  } catch (err) {
    return { valid: false, errors: [{ message: err.message }] };
  }
}

// ---------------------------------------------------------------------------
// Validation bar
// ---------------------------------------------------------------------------

/**
 * Update the persistent validation bar state.
 * @param {'valid'|'errors'|'stale'|'validating'} barState
 * @param {Array<{line?: number, message?: string}>} errors
 */
export function updateValidationBar(barState, errors = []) {
  _validationState = barState;
  const bar = document.getElementById('validationBar');
  if (!bar) return;
  bar.hidden = false;
  bar.className = `validation-bar validation-bar-${barState}`;

  const icon = bar.querySelector('.validation-bar-icon');
  const msg = bar.querySelector('.validation-bar-message');
  if (!icon || !msg) return;

  msg.style.cursor = '';
  msg.onclick = null;

  switch (barState) {
    case 'valid':
      icon.textContent = '\u2713';
      msg.textContent = 'Valid';
      break;
    case 'errors': {
      icon.textContent = '\u2717';
      const first = errors[0];
      const lineInfo = first?.line ? ` \u2014 Line ${first.line}: ${first.message || 'error'}` : `: ${first?.message || 'Unknown error'}`;
      msg.textContent = `${errors.length} error${errors.length > 1 ? 's' : ''}${lineInfo}`;
      if (first?.line) {
        msg.style.cursor = 'pointer';
        msg.onclick = () => revealLine(first.line);
      }
      break;
    }
    case 'stale':
      icon.textContent = '\u25CB';
      msg.textContent = 'Modified \u2014 save to re-validate';
      break;
    case 'validating':
      icon.textContent = '\u27F3';
      msg.textContent = 'Validating...';
      break;
  }
}

/** Reset validation state (e.g. when switching tabs). */
export function resetValidationState() {
  _validationState = null;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function _monacoLanguage(lang) {
  switch (lang) {
    case "python": return "ironpython";
    case "html": return "html";
    case "json": return "json";
    case "css": return "css";
    case "javascript": return "javascript";
    default: return "plaintext";
  }
}

function _updateDirtyBadge(tabId, isDirty) {
  // Toggle .dirty on the tab element (drives close-button dot indicator)
  const tabEl = document.querySelector(`.ws-tab[data-tab-id="${tabId}"]`);
  if (tabEl) tabEl.classList.toggle('dirty', isDirty);

}

const LANG_DISPLAY = {
  python: 'IronPython',
  json: 'JSON',
  html: 'HTML',
  javascript: 'JavaScript',
  css: 'CSS',
};

export function updateStatusBarLang(lang) {
  const sbLang = document.getElementById('sbLang');
  if (sbLang) sbLang.textContent = LANG_DISPLAY[lang] || lang || 'Plain Text';
}

// Keep internal alias for use inside this module
const _updateStatusBarLang = updateStatusBarLang;

function _updateStatusBarIndent(editor) {
  const sbIndent = document.getElementById('sbIndent');
  if (!sbIndent || !editor) return;
  const opts = editor.getOptions();
  const insertSpaces = editor.getOption(window.monaco.editor.EditorOption.insertSpaces);
  const tabSize = editor.getOption(window.monaco.editor.EditorOption.tabSize);
  sbIndent.textContent = insertSpaces ? `Spaces: ${tabSize}` : `Tab Size: ${tabSize}`;
}
